/**
 * ReviewDetailPage — `/admin/needs-review/:kind/:id` (BON-381).
 *
 * Answers three questions fast: what the system thinks went wrong, what
 * actually happened, and where to go to fix it. It is also the ONLY place a
 * flag can be resolved or reopened, because both require a note and a queue row
 * has nowhere to put one.
 *
 * The page renders fully even when Stripe could not be reached — the money card
 * degrades to an honest "couldn't reach Stripe" and everything else stands. The
 * review queue is the mitigation for payments having gone wrong; a Stripe
 * outage must not be able to take it down.
 */
import { useParams } from 'react-router-dom';

import { useViewport } from '../../hooks/useViewport';

import ReviewComposerSlot from './components/ReviewComposerSlot';
import ReviewDetailPlaceholder from './components/ReviewDetailPlaceholder';
import ReviewDetailShell from './components/ReviewDetailShell';
import { ReviewDetailActions } from './ReviewDetailActions';
import { ReviewDetailLayout } from './ReviewDetailLayout';
import type { ReviewDetail, ReviewKind } from './reviewTypes';
import { useCanWriteReview } from './useCanWriteReview';
import { useReviewDetailState } from './useReviewDetailState';

import '../dashboards/dashboards.css';

function isReviewKind(value: string | undefined): value is ReviewKind {
  return value === 'booking' || value === 'checkout';
}

/** The page title: a booking is identified by its confirmation code, a checkout
 *  session by the breadcrumb above it (there is no code to show). */
function titleFor(detail: ReviewDetail): string {
  return detail.kind === 'booking' ? `Booking ${detail.reference}` : 'Checkout session';
}

export default function ReviewDetailPage() {
  const { kind, id } = useParams<{ kind: string; id: string }>();
  const { isPhone } = useViewport();
  const routed = isReviewKind(kind) && Boolean(id);
  const s = useReviewDetailState(isReviewKind(kind) ? kind : 'booking', id ?? '');
  const detail = s.detail;
  const canWrite = useCanWriteReview(detail);

  if (!routed) {
    return <ReviewDetailPlaceholder isPhone={isPhone} message="That is not a reviewable item." />;
  }
  if (s.loading) return <ReviewDetailPlaceholder isPhone={isPhone} message={null} />;
  if (!detail) {
    return (
      <ReviewDetailPlaceholder
        isPhone={isPhone}
        message={s.error ?? 'That flagged item could not be loaded.'}
      />
    );
  }

  const composing = s.composer !== null;
  const actions = (
    <ReviewDetailActions
      detail={detail}
      canWrite={canWrite}
      composing={composing}
      onCompose={s.openComposer}
    />
  );

  return (
    <ReviewDetailShell
      crumb={`Customers › Needs review › ${detail.reference}`}
      title={titleFor(detail)}
      isPhone={isPhone}
      actions={actions}
    >
      {/* U1: the ONLY thing that tells a screen-reader user their resolve landed.
          A completed transition changes a pill, swaps a button and adds a history
          row — all of it silent without this. `polite` so it waits for a pause
          rather than cutting across whatever is being read. */}
      <div aria-live="polite" role="status" className="nb-sr-only">
        {s.announcement}
      </div>
      <ReviewDetailLayout
        detail={detail}
        isPhone={isPhone}
        composer={<ReviewComposerSlot s={s} />}
      />
      {isPhone && (
        <ReviewDetailActions
          detail={detail}
          canWrite={canWrite}
          composing={composing}
          onCompose={s.openComposer}
          variant="bar"
        />
      )}
    </ReviewDetailShell>
  );
}
